import { SerializedError } from '@reduxjs/toolkit'
import { useGetCurrenciesQuery, useSetCurrencyMutation } from 'api/currencyApi'
import { logger } from 'server/utils/logger'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { ClientRequestServiceError } from 'shared/services/clientRequestService'
import { Currency } from 'shared/types/Currency'

export type SelectedCurrency = {
  isoCode: string
  displaySymbol: string
  disambiguatedSymbol: string
}

export type CurrencyHookInfo = {
  availableCurrencies?: Currency[]
  getCurrenciesErrors?: ClientRequestServiceError | SerializedError | undefined
  getCurrenciesLoading?: boolean
  loading: boolean
  selectedCurrency?: Currency
  setCurrency: (isoCode: string) => Promise<string>
}

const useCurrency = (): CurrencyHookInfo => {
  const { currencyCode: selectedCurrencyCode } = useShoppingRegionContext()
  const [setCurrencyMutation, { isLoading: loadingSetCurrency }] = useSetCurrencyMutation()
  const {
    data: getCurrenciesData,
    error: getCurrenciesErrors,
    isLoading: getCurrenciesLoading,
  } = useGetCurrenciesQuery()

  const setCurrency = async (isoCode: string) => {
    try {
      const res = await setCurrencyMutation(isoCode).unwrap()
      return res.success ? isoCode : ''
    } catch (error) {
      logger.error(error, 'Error setting currency:')
      return ''
    }
  }

  const selectedCurrency = getCurrenciesData?.currencies.find(
    (currency) => currency.isoCode === selectedCurrencyCode,
  )

  return {
    availableCurrencies: getCurrenciesData?.currencies,
    getCurrenciesErrors,
    getCurrenciesLoading,
    loading: loadingSetCurrency || getCurrenciesLoading,
    selectedCurrency,
    setCurrency,
  }
}

export default useCurrency
